import axios, { isAxiosError } from "axios";
import type { AxiosResponse } from "axios";

import { errorTypes } from "../stateEnums";
import { isPasswordEncrypted } from "../crypto";
import { getApiErrorMessage, headers, host } from "./shared";
import type {
  DeleteStudentsResponse,
  SchoolStudentInput,
  StudentValidationErrors,
} from "../../types/school";

type AccessToken = string | null | undefined;

type BatchStudentErrorBody = {
  error?: string | StudentValidationErrors;
  error_type?: string;
};

export const createStudent = async (
  student: SchoolStudentInput,
  schoolId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<void>> => {
  try {
    if (!isPasswordEncrypted(student.password)) {
      throw new Error("Password must be encrypted");
    }

    return await axios.post(
      `${host}/api/schools/${schoolId}/students`,
      { school_student: student },
      headers(accessToken),
    );
  } catch (error) {
    const errorMessage = getApiErrorMessage(error);
    throw errorMessage ? new Error(errorMessage) : error;
  }
};

export const createStudents = async (
  students: SchoolStudentInput[],
  schoolId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<{ job_id: string }>> => {
  try {
    if (
      !students.every(
        (student) =>
          !student?.password || isPasswordEncrypted(student.password),
      )
    ) {
      throw new Error("All passwords must be encrypted");
    }

    return await axios.post(
      `${host}/api/schools/${schoolId}/students/batch`,
      { school_students: students },
      headers(accessToken),
    );
  } catch (error) {
    const data = isAxiosError<BatchStudentErrorBody>(error)
      ? error.response?.data
      : undefined;
    const errorData = data?.error;
    const errorType = data?.error_type;

    // If the error is an object stringify it, else pass back as is. This is to ensure that the error message is always a string
    // and we don't stringify a string. Axios will strip out any custom properties which is annoying...ask me how I know.
    if (errorType === "validation_error") {
      throw JSON.stringify(errorData);
    } else if (errorTypes[errorType]) {
      // We only need to know the type of the error, not the message
      throw errorTypes[errorType];
    } else {
      throw new Error("An unknown error occurred");
    }
  }
};

export const putStudentDetails = async (
  student: SchoolStudentInput,
  studentId: string,
  schoolId: string,
  accessToken: AccessToken,
): Promise<void> => {
  let response: AxiosResponse<void>;
  try {
    if (student.password && !isPasswordEncrypted(student.password)) {
      throw new Error("Password must be encrypted");
    }

    response = await axios.put(
      `${host}/api/schools/${schoolId}/students/${studentId}`,
      { school_student: student },
      headers(accessToken),
    );
    return response.data;
  } catch (error) {
    const errorMessage = getApiErrorMessage(error);
    throw errorMessage ? new Error(errorMessage) : error;
  }
};

export const deleteStudent = async (
  studentId: string,
  schoolId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<DeleteStudentsResponse>> => {
  try {
    return await axios.delete(
      `${host}/api/schools/${schoolId}/students/${studentId}`,
      headers(accessToken),
    );
  } catch (error) {
    const errorMessage = getApiErrorMessage(error);
    throw errorMessage ? new Error(errorMessage) : error;
  }
};

export const deleteStudents = async (
  studentIds: string[],
  schoolId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<DeleteStudentsResponse>> => {
  try {
    return await axios.delete(
      `${host}/api/schools/${schoolId}/students/batch`,
      {
        ...headers(accessToken),
        data: { student_ids: studentIds },
      },
    );
  } catch (error) {
    const errorMessage = getApiErrorMessage(error);
    throw errorMessage ? new Error(errorMessage) : error;
  }
};
